import type { MerchantCategoryRelationship, ServiceItem } from "~/lib/types";

export const CATALOG_ROOT_KEY = "CAT-ROOT";

export const collectDescendantIds = (
  rootId: string,
  relationships: readonly MerchantCategoryRelationship[],
): Set<string> => {
  const visited = new Set<string>();
  const visit = (id: string): void => {
    if (visited.has(id)) return;
    visited.add(id);
    for (const r of relationships) {
      if (r.related_item_id === id) {
        visit(r.item_id);
      }
    }
  };
  visit(rootId);
  return visited;
};

export const collectAncestorIds = (
  itemId: string,
  relationships: readonly MerchantCategoryRelationship[],
): string[] => {
  const chain: string[] = [];
  const seen = new Set<string>();
  const walk = (id: string): void => {
    if (seen.has(id)) return;
    seen.add(id);
    chain.push(id); // eslint-disable-line fp/no-mutating-methods
    const edge = relationships.find(r => r.item_id === id);
    if (edge) walk(edge.related_item_id);
  };
  walk(itemId);
  return chain;
};

export const sortSiblings = (
  items: readonly ServiceItem[],
  orderFor: (item: ServiceItem) => number | undefined,
): ServiceItem[] =>
  // eslint-disable-next-line fp/no-mutating-methods
  [...items].sort((a, b) => {
    const oa = orderFor(a) ?? 0;
    const ob = orderFor(b) ?? 0;
    if (oa !== ob) return oa - ob;
    return a.item_name.localeCompare(b.item_name);
  });

export const findTopLevelItems = (
  items: readonly ServiceItem[],
  relationships: readonly MerchantCategoryRelationship[],
): ServiceItem[] => {
  const root = items.find(i => i.item_key === CATALOG_ROOT_KEY);
  if (root) {
    const childEdges = relationships.filter(r => r.related_item_id === root.id);
    const orderById = new Map<string, number>();
    for (const edge of childEdges) {
      orderById.set(edge.item_id, edge.display_order ?? 0);
    }
    const children = items.filter(i => orderById.has(i.id));
    return sortSiblings(children, i => orderById.get(i.id));
  }

  const childIds = new Set(relationships.map(r => r.item_id));
  const orphans = items.filter(i => !childIds.has(i.id));
  return sortSiblings(orphans, () => 0);
};

export const findDirectChildren = (
  parentId: string,
  items: readonly ServiceItem[],
  relationships: readonly MerchantCategoryRelationship[],
): ServiceItem[] => {
  const childEdges = relationships.filter(r => r.related_item_id === parentId);
  const orderById = new Map<string, number>();
  for (const edge of childEdges) {
    orderById.set(edge.item_id, edge.display_order ?? 0);
  }
  const children = items.filter(i => orderById.has(i.id));
  return sortSiblings(children, i => orderById.get(i.id));
};
